<template>
  <v-dialog v-model="showJetpackTable" max-width="600px" persistent>
    <v-card :loading="jetpackLoading">
      <v-card-title color="rgba(0, 0, 0, 0)" flat>
        <span class="font-weight-regular">Jetpack</span>
        <v-divider class="mx-4" inset vertical></v-divider>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          class="mr-2"
          clearable
          dense
          hide-details
          label="Search Jetpack"
          outlined
          prepend-inner-icon="mdi-magnifyuserSpesific"
        ></v-text-field>

        <v-spacer></v-spacer>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="mx-2"
              @click="openJetpackModal"
              fab
              small
              dark
              color="blue-grey"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon dark>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>New jetpack version with l4t</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="mx-2"
              @click="openSync"
              fab
              small
              dark
              color="indigo"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon dark>mdi-playlist-check</v-icon>
            </v-btn>
          </template>
          <span>Check for missing JetPack releases</span>
        </v-tooltip>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeJetpack">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="jetpackVersionList"
        :search="search"
      >
        <template v-slot:[`item.actions`]="{ item }">
          <!-- eslint-disable-next-line -->
          <v-btn
            v-for="(action, idx) in actionButtons"
            :key="idx"
            icon
            @click="action.func(item)"
          >
            <v-icon :color="action.color || 'black'">{{ action.icon }}</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
    <create-jetpack-view
      :showJetpackDialog="showJetpackDialog"
      @close-jetpack="closeJetpackDialog"
      @fetch-jetpack-list="fetchJetpack"
      :showUpdateOrEdit="updateJetpack"
      :jetpackInfo="selectedJetpack"
    >
    </create-jetpack-view>

    <!-- Missing / inconsistent rows. Adding never touches existing rows: tags link to them by id. -->
    <v-dialog v-model="syncDialog" max-width="560px">
      <v-card :loading="syncLoading">
        <v-card-title class="font-weight-regular">
          JetPack releases check
          <v-spacer></v-spacer>
          <v-btn icon @click="syncDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text v-if="syncPlan">
          <div class="subtitle-2 mb-1">Missing ({{ syncPlan.missing.length }})</div>
          <div v-if="!syncPlan.missing.length" class="mb-4 grey--text">
            Every known L4T release already has a row.
          </div>
          <v-simple-table v-else dense class="mb-4">
            <tbody>
              <tr v-for="row in syncPlan.missing" :key="row.l4t">
                <td>L4T {{ row.l4t }}</td>
                <td>JetPack {{ row.version }}</td>
                <td class="grey--text">{{ row.label }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="subtitle-2 mb-1">Looks wrong ({{ syncPlan.review.length }})</div>
          <div v-if="!syncPlan.review.length" class="grey--text">Nothing to review.</div>
          <template v-else>
            <div class="caption grey--text mb-1">
              Not changed automatically. Edit the row instead of deleting it: tags are linked to it.
            </div>
            <v-simple-table dense>
              <tbody>
                <tr v-for="row in syncPlan.review" :key="row.id">
                  <td>#{{ row.id }} L4T {{ row.l4t }}</td>
                  <td>{{ row.version }} &rarr; <b>{{ row.expected }}</b></td>
                  <td class="text-right">
                    <v-btn icon small @click="editFromReview(row)">
                      <v-icon small color="grey">mdi-pencil-circle-outline</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="syncDialog = false">Close</v-btn>
          <v-btn
            color="indigo"
            dark
            :disabled="!syncPlan || !syncPlan.missing.length"
            :loading="syncApplying"
            @click="applySync"
          >
            Add {{ syncPlan ? syncPlan.missing.length : 0 }} missing
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import converter from '@/mixins/converter'
import Swal from 'sweetalert2'
import { apiService } from '../../service/api.service'
import CreateJetpackView from './CreateJetpackView.vue'
import { mapGetters } from 'vuex'

export default converter.extend({
  computed: {
    ...mapGetters(['jetpackVersionList', 'jetpackLoading'])
  },
  components: { CreateJetpackView },
  data () {
    return {
      loading: false,
      rules: [(v) => !!v || 'Field is required'],
      valid: true,
      showJetpackDialog: false,
      search: null,
      updateJetpack: false,
      selectedJetpack: null,
      syncDialog: false,
      syncLoading: false,
      syncApplying: false,
      syncPlan: null,
      actionButtons: [
        {
          icon: 'mdi-pencil-circle-outline',
          color: 'grey',
          func: this.update_jetpack
        },
        {
          icon: 'mdi-delete-outline',
          color: 'red',
          func: this.delete_jetpack,
          condition: {
            deleted: false
          }
        }
      ],
      headers: [
        {
          text: 'ID',
          value: 'id',
          align: 'center'
        },
        {
          text: 'Label',
          value: 'label',
          align: 'center'
        },
        { text: 'Jetpack Version', value: 'version', align: 'center' },
        { text: 'L4T', value: 'l4t', align: 'center' },
        { text: 'Min Driver Version', value: 'min_driver_version', align: 'center' },
        { text: 'Actions', value: 'actions', align: 'center' }
      ]
    }
  },
  props: {
    showJetpackTable: Boolean
  },
  mounted () {
    this.fetchJetpack()
  },

  methods: {
    closeJetpack () {
      this.$emit('close-jetpack-table')
    },

    async fetchJetpack () {
      this.$store.dispatch('getJetpackVersionList')
    },
    openJetpackModal () {
      this.showJetpackDialog = true
      this.updateJetpack = false
    },

    update_jetpack: function (item) {
      this.showJetpackDialog = true
      this.selectedJetpack = item
      this.updateJetpack = true
    },

    async delete_jetpack (item) {
      Swal.fire({
        title: 'Are you sure?',
        text: 'You wont be able to revert this!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!'
      })
        .then(async (result) => {
          if (result.isConfirmed) {
            await apiService
              .delete(`admin/jetpack-delete/${item.id}`)
              .catch((err) => {
                Swal.showValidationMessage(`Request failed: ${err}`)
              })
            this.fetchJetpack()
            Swal.fire('Deleted!', 'Your file has been deleted.', 'success')
          }
        })
        .finally(() => (this.deviceLoading = false))
    },

    async edit_jetpack () {
      if (this.$refs.tagForm.validate()) {
        this.loading = true
        const jetpack = {
          label: this.label,
          version: this.version,
          l4t: this.l4t
        }
        await apiService
          .post('dockers/jetpack', jetpack)
          .then((res) => {
            this.$emit('close-jetpack')
            const form = this.$refs.tagForm
            if (form) {
              form.reset()
              form.resetValidation()
            }
          })
          .finally(() => {
            this.loading = false
          })
      }
    },

    closeJetpackDialog () {
      this.showJetpackDialog = false
      if (this.syncDialog) this.loadSyncPlan()
    },

    openSync () {
      this.syncDialog = true
      this.loadSyncPlan()
    },

    async loadSyncPlan () {
      this.syncLoading = true
      try {
        const res = await apiService.get('admin/jetpack-sync')
        this.syncPlan = res.data.data
      } catch (err) {
        this.syncPlan = null
        Swal.fire('Check failed', String(err), 'error')
      } finally {
        this.syncLoading = false
      }
    },

    async applySync () {
      this.syncApplying = true
      try {
        const res = await apiService.post('admin/jetpack-sync')
        const added = (res.data.data && res.data.data.added) || []
        this.fetchJetpack()
        await this.loadSyncPlan()
        Swal.fire('Done', `${added.length} JetPack release(s) added.`, 'success')
      } catch (err) {
        Swal.fire('Could not add', String(err), 'error')
      } finally {
        this.syncApplying = false
      }
    },

    editFromReview (row) {
      const item = (this.jetpackVersionList || []).find((j) => j.id === row.id)
      if (item) this.update_jetpack(item)
    }
  }
})
</script>

<style>
</style>
